import React, { useEffect, useState } from 'react'
import {
	Box, Button, Chip, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material'
import { AppDialog, AppDialogHeader, AppDialogBody, AppDialogFooter, AppDialogCancel, AppNote, AppEmptyLine, AppTableSkeleton } from '@components'
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { dsColorNew } from '@palette'
import { Link as RouterLink } from 'react-router-dom'
import { OpenInNew as OpenIcon } from 'Pages/DsRepricing/@components/icons'
import { activityActions } from 'Pages/DsRepricing/Activity/@services/useActivity'
import { headCellSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import { displayDay } from '@utils'
import { toneChipSx } from 'Pages/DsRepricing/@components/tones'

export const OUTCOME_STYLE = {
	applied:          { tone: 'ok',      label: 'applied' },
	capped:           { tone: 'warn',    label: 'capped' },
	tethered:         { tone: 'info',    label: 'tethered' },
	no_price_change:  { tone: 'neutral', label: 'no change' },
	no_strategy:      { tone: 'neutral', label: 'no strategy' },
	no_rate_card:     { tone: 'neutral', label: 'no rate card' },
	quarantined:      { tone: 'accent',  label: 'quarantined' },
	pms_error:        { tone: 'err',     label: 'PMS error' },
	not_sent:         { tone: 'info',    label: 'not sent' },
}

export const eur = (v) => (v == null ? '—' : `${Number(v).toFixed(2)} €`)
export const fmtDay = (iso) => displayDay(iso)

// The three faces of "not sent" (CU-869f03vek) read as one word here: this list is a bucket, not the Activity.
const SENT_LABEL = { ok: 'sent', error: 'refused', dry_run: 'simulated', not_sent: 'not sent',
                     waiting_tasks: 'not sent', pending_resend: 'not sent', sibling_sent: 'not sent',
                     superseded: 'superseded', unchanged: 'already set' }

/**
 * Decisions one by one: a row per decision with its scope and the
 * price before and after, biggest change first. Opened from a pass on
 * Executions (`query = { execution_id }`) and from a day on the
 * Calendar (`query = { date_from, date_to }`); `type` narrows to one
 * outcome, or `query.decided_not_sent` to what never went out.
 *
 * Reads `scope.*` as the webback serialises it — the first version
 * read flat `scope_office` fields that do not exist and showed "—" in
 * four columns (Curro, 2026-10-03).
 */
export default function ChangesDialog({ title, type, query, activityHref, activityLabel, emptyText, onClose }) {
	const [state, setState] = useState({ loading: true, items: [], total: 0 })
	const key = JSON.stringify({ ...query, type })
	useEffect(() => {
		let alive = true
		setState({ loading: true, items: [], total: 0 })
		activityActions.listDecisions({ ...query, ...(type ? { event_type: type } : {}) })
			.then(b => alive && setState({ loading: false, items: b.items || [], total: b.total || 0 }))
			.catch(e => alive && setState({ loading: false, items: [], total: 0,
			                                error: e?.message || 'Could not load the decisions' }))
		return () => { alive = false }
	}, [key])  // eslint-disable-line react-hooks/exhaustive-deps
	const styleKey = type || (query?.decided_not_sent ? 'not_sent' : null)
	const style = OUTCOME_STYLE[styleKey] || { tone: 'neutral', label: styleKey || 'decisions' }
	const priced = !type || ['applied', 'capped', 'tethered', 'quarantined', 'no_price_change'].includes(type)
	return (
		<AppDialog open onClose={onClose} maxWidth="md">
			<AppDialogHeader icon="list-check" onClose={onClose}
			                 title={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25 }}>
				                 <Chip size="small" label={style.label} sx={toneChipSx(style.tone)}/>
				                 {title}
			                 </Box>}
			                 subtitle={state.loading ? '' : `${state.items.length}${state.total > state.items.length ? ` of ${state.total}` : ''} decisions`}/>
			<AppDialogBody sx={{ ...dialogBodySx, p: 0, borderTop: `1px solid ${dsColorNew.grayBorder}` }}>
				{state.loading && <AppTableSkeleton rows={5} columns={priced ? 8 : 5} sx={{ m: 2 }}/>}
				{state.error && <AppNote severity="error" sx={{ m: 2 }}>{state.error}</AppNote>}
				{!state.loading && !state.error && state.items.length === 0 && (
					<AppEmptyLine sx={{ px: 3 }}>{emptyText || 'Nothing of this kind here.'}</AppEmptyLine>
				)}
				{state.items.length > 0 && (
					<Table size="small" stickyHeader sx={{ '& td': { fontSize: 13, py: 0.75 } }}>
						<TableHead>
							<TableRow sx={{ '& th': { ...headCellSx, fontSize: 11 } }}>
								<TableCell>Office</TableCell>
								<TableCell>ACRISS</TableCell>
								<TableCell>Pickup</TableCell>
								<TableCell align="right">Days</TableCell>
								{priced && <TableCell align="right">Before</TableCell>}
								{priced && <TableCell align="right">After</TableCell>}
								{priced && <TableCell align="right">Δ</TableCell>}
								<TableCell>{priced ? 'Sent' : 'Why'}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{state.items.map(r => {
								const scope = r.scope || {}
								const before = r.previous_price, after = r.pushed_price ?? r.new_price
								const d = before != null && after != null ? after - before : null
								return (
									<TableRow key={r.id} hover>
										<TableCell sx={{ fontWeight: 700 }}>{scope.office || '—'}</TableCell>
										<TableCell sx={{ fontFamily: 'inherit', fontWeight: 700 }}>{scope.sipp || '—'}</TableCell>
										<TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDay(scope.pickup_date)}</TableCell>
										<TableCell align="right" sx={{ fontFamily: 'inherit' }}>{scope.duration_days ?? '—'}</TableCell>
										{priced && <TableCell align="right" sx={{ fontFamily: 'inherit', color: dsColorNew.textMuted }}>{eur(before)}</TableCell>}
										{priced && <TableCell align="right" sx={{ fontFamily: 'inherit', fontWeight: 700 }}>{eur(after)}</TableCell>}
										{priced && (
											<TableCell align="right" sx={{ fontFamily: 'inherit', fontWeight: 700,
											                                color: d == null ? dsColorNew.textSubtle : d > 0 ? dsColorNew.successText : d < 0 ? dsColorNew.dangerRed : dsColorNew.textMuted }}>
												{d == null ? '—' : `${d > 0 ? '+' : ''}${d.toFixed(2)}`}
											</TableCell>
										)}
										<TableCell sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
											{priced
												? (SENT_LABEL[r.write_status] || (r.payload?.mode === 'dry_run' ? 'simulated' : '—'))
												: (r.note || r.error_message || r.error_code || '—')}
										</TableCell>
									</TableRow>
								)
							})}
						</TableBody>
					</Table>
				)}
			</AppDialogBody>
			<AppDialogFooter>
				{activityHref && (
					<Button component={RouterLink} to={activityHref} endIcon={<OpenIcon/>}
					        variant="text" sx={{ height: 36, borderRadius: '8px', textTransform: 'none', fontSize: 14, fontWeight: 600, mr: 'auto', color: dsColorNew.appBlue }}>
						{activityLabel || 'Open in Activity'}
					</Button>
				)}
				<AppDialogCancel onClick={onClose}>Close</AppDialogCancel>
			</AppDialogFooter>
		</AppDialog>
	)
}
